<template>
  <div class="configurations-table">
    <mi-table
      ref="miTable"
      :columns="table.columns"
      :filter="configurationsSearchString"
      :loading="areConfigurationsLoading"
      :rows="table.rows"
      :custom-header="true"
      :on-change-sort-table-column-visibility="tableCallbacks.onChangeSortTableColumnVisibility"
      :on-change-sort-table-direction="tableCallbacks.onChangeSortTableDirection"
      class="configurations-table q-px-lg q-pb-lg"
      column-sort-order="da"
      loading-label="Loading configurations. Please wait ..."
      no-data-label="No configurations found"
      row-key="id"
      hide-pagination
      sticky-header
      virtual-scroll
      striped
    >
      <template #top>
        <div class="configurations-table-header">
          <div class="configurations-table-header__top-row">
            <span class="configurations-table-header__title">Configurations</span>
          </div>
          <div class="configurations-table-header__master-checkbox">
            <div
              class="configurations-table-header__master-control"
              :class="{ 'configurations-table-header__master-dropdown--active': hasAnySelected }"
            >
              <mi-checkbox
                :model-value="masterCheckboxState"
                indeterminate-value="indeterminate"
                :toggle-indeterminate="false"
                @click="handleMasterCheckboxClick"
              ></mi-checkbox>
              <mi-btn-dropdown
                flat
                dense
                dropdown-icon="caret-down"
                no-icon-animation
                class="configurations-table-header__master-dropdown"
                :class="{ 'configurations-table-header__master-dropdown--active': hasAnySelected }"
              >
                <div class="configurations-table-header__select-dropdown">
                  <mi-btn flat @click="handleSelectAllCurrentPage()">
                    All
                  </mi-btn>

                  <mi-btn flat @click="handleSelectMineOnly()">
                    Mine only
                  </mi-btn>

                  <mi-btn flat @click="handleSelectFromToday()">
                    From today
                  </mi-btn>
                </div>
              </mi-btn-dropdown>
            </div>
            <mi-separator v-if="configurationsSelected.length > 0" vertical class="q-mx-sm"></mi-separator>
            <div v-if="configurationsSelected.length > 0" class="configurations-table-header__action-buttons">
              <mi-btn
                color="primary"
                @click="showSaveNewListModal = true"
              >
                Create new list
              </mi-btn>
              <mi-btn
                outline
                @click="showAddConfigurationsToListModal = true"
              >
                Add to list
              </mi-btn>
            </div>
          </div>
        </div>
      </template>

      <!-- Name column -->
      <template #body-cell-name="{ value, row }">
        <td class="text-left">
          <mi-checkbox
            :model-value="isSelected(row.id)"
            class="q-pr-sm"
            @click="toggleSelection(row.id)"
          ></mi-checkbox>
          <span>{{ value }}</span>
        </td>
      </template>

      <!-- Description column -->
      <template #body-cell-description="{ value, row }">
        <!-- Validation status -->
        <mi-td>
          <div class="flex items-center no-wrap">
            <span
              class="configurations-table__description ellipsis-2-lines q-mr-xs relative-position text-left"
            >
              {{ value }}
            </span>
            <div class="flex no-wrap q-ml-auto">
              <!-- Open configuration button -->
              <mi-tr-action-btn icon="exit" @click="openConfiguration(row.id)">
                <mi-tooltip> Open </mi-tooltip>
              </mi-tr-action-btn>

              <!-- Delete configuration button -->
              <mi-tr-action-btn
                icon="trash"
                :disabled="!hasPermissionToDelete(row)"
                @click="openDeleteConfigurationDialog(row)"
              >
                <mi-tooltip :hidden="!hasPermissionToDelete(row) ">
                  Delete
                </mi-tooltip>
              </mi-tr-action-btn>
            </div>
          </div>
        </mi-td>
      </template>
    </mi-table>
  </div>
  <div
    v-if="!!table.rows.length"
    class="configurations-table__pagination"
  >
    <mi-pagination
      :model-value="pagination.currentPage"
      :max="pagination.totalPages"
      :max-pages="pagination.maxNavPage"
      size="14px"
      boundary-links
      direction-links
      boundary-numbers
      ellipses
      @update:model-value="handlePaginationChange"
    ></mi-pagination>
  </div>
  <!-- Configuration delete dialog -->
  <configuration-delete-dialog
    v-model="isDeleteConfigurationDialogShown"
    :configuration="configurationToDelete"
    @delete:configuration="handleConfigurationDeletedEvent"
  ></configuration-delete-dialog>

  <learn-more-pac-modal :show-modal="showLearnMoreModal" @update:show-modal="showLearnMoreModal = false">
  </learn-more-pac-modal>

  <save-new-list-modal
    :show-modal="showSaveNewListModal"
    :selected-configurations-ids-set="configurationsSelected"
    :handle-deselect-all="handleDeselectAll"
    @update:show-modal="showSaveNewListModal = false"
  >
  </save-new-list-modal>
  <add-configurations-to-list-modal
    :handle-deselect-all="handleDeselectAll"
    :selected-configurations-ids-set="configurationsSelected"
    :show-modal="showAddConfigurationsToListModal"
    @update:show-modal="showAddConfigurationsToListModal = false"
  >
  </add-configurations-to-list-modal>
</template>

<script>
  import { computed, onMounted, onUnmounted, ref, watch, getCurrentInstance } from 'vue'
  import { useStore } from 'vuex'
  import { useRouter } from 'vue-router'
  import { usePagination } from '@/composables/usePagination'
  import { useTable } from '@/composables/useTable'
  import ConfigurationDeleteDialog from '@/components/pac/configuration/dialogs/ConfigurationDeleteDialog.vue'
  import MiPagination from '@/packages/@mi-library/MiPagination/MiPagination.vue'
  import { getConfigurations } from '@/api'
  import { configurationsTableColumns } from '@/components/pac/configurations/utils'
  import { PAC_DELETED_CONFIG } from '@/utils/analytics/constants'
  import { PAC_CONFIGURATION_ROUTE } from '@/router/routeNames'
  import { SET_CONFIGURATIONS_SEARCH_STRING } from '@/store/modules/pac/mutationTypes'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import MiBtnDropdown from '@/packages/@mi-library/MiBtnDropdown/MiBtnDropdown.vue'
  import MiBtn from '@/packages/@mi-library/MiBtn/MiBtn.vue'
  import MiCheckbox from '@/packages/@mi-library/MiCheckbox/MiCheckbox.vue'
  import MiSeparator from '@/packages/@mi-library/MiSeparator/MiSeparator.vue'
  import SaveNewListModal from '@/components/pac/saved-lists/SaveNewListModal.vue'
  import AddConfigurationsToListModal from '@/components/pac/saved-lists/AddConfigurationsToListModal.vue'
  import LearnMorePacModal from '../LearnMorePacModal.vue'

  export default {
    name: 'ConfigurationsTable',
    components: {
      AddConfigurationsToListModal,
      SaveNewListModal,
      MiCheckbox,
      MiSeparator,
      MiBtn,
      MiBtnDropdown,
      LearnMorePacModal,
      MiPagination,
      ConfigurationDeleteDialog
    },
    setup() {
      const areConfigurationsLoading = ref(false)
      const configurationToDelete = ref(null)
      const isDeleteConfigurationDialogShown = ref(false)
      const { proxy } = getCurrentInstance()
      const showLearnMoreModal = ref(
        proxy.$q.localStorage.getItem(process.env.VUE_APP_STORAGE_KEY_LEARN_MORE_PAC_MODAL) === null
      )

      const savedListsModal = { SAVE: { key: 'saveNew' }, UPDATE: { key: 'addConfigurations' } }

      const configurationsSelected = ref([])
      const selectedSet = computed(() => new Set(configurationsSelected.value))
      const showSaveNewListModal = ref(false)
      const showAddConfigurationsToListModal = ref(false)

      const totalConfigurations = ref(0)

      const { pagination, handleChange: handlePaginationChange } = usePagination({ pageSize: 20 })
      const { table, callbacks: tableCallbacks } = useTable(configurationsTableColumns)

      const router = useRouter()
      const { state: { user, pac: pacStore }, commit } = useStore()
      const usePacMutation = name => `pac/${ name }`
      const configurationsSearchString = computed(() => pacStore.configurationsSearchString)

      const currentPageIds = computed(() => table.rows.map(r => r.id))
      const currentPageCount = computed(() => currentPageIds.value.length)

      const selectedOnCurrentPageCount = computed(() => {
        const currentPageIdSet = new Set(currentPageIds.value)
        const selectedOnCurrentPage = configurationsSelected.value.filter(id => currentPageIdSet.has(id))
        return selectedOnCurrentPage.length
      })

      const hasAnySelected = computed(() => configurationsSelected.value.length > 0)

      const hasPermissionToDelete = configuration => {
        const { authorizedUsers = [] } = configuration
        if (authorizedUsers.length) return authorizedUsers.includes(user.username)
        return true
      }

      const isSelected = id => selectedSet.value.has(id)

      const addSelection = id => {
        if (!selectedSet.value.has(id)) configurationsSelected.value = [...configurationsSelected.value, id]
      }

      const removeSelection = id => {
        if (selectedSet.value.has(id)) {
          configurationsSelected.value = configurationsSelected.value.filter(
            configSelectedId => configSelectedId !== id
          )
        }
      }

      const toggleSelection = id => {
        if (selectedSet.value.has(id)) removeSelection(id)
        else addSelection(id)
      }

      const handleSelectAllCurrentPage = () => {
        const configurationsSelectedSet = new Set(configurationsSelected.value)
        currentPageIds.value.forEach(id => configurationsSelectedSet.add(id))
        configurationsSelected.value = Array.from(configurationsSelectedSet)
      }

      const handleSelectMineOnly = () => {
        const mineIds = table.rows
          .filter(row => row.creator === user.username)
          .map(row => row.id)
        const configurationsSelectedSet = new Set(configurationsSelected.value)
        mineIds.forEach(id => configurationsSelectedSet.add(id))
        configurationsSelected.value = Array.from(configurationsSelectedSet)
      }

      const handleSelectFromToday = () => {
        const today = new Date().toISOString().slice(0, 10)
        const todayIds = table.rows
          .filter(row => row.createdDate?.startsWith(today))
          .map(row => row.id)
        const configurationsSelectedSet = new Set(configurationsSelected.value)
        todayIds.forEach(id => configurationsSelectedSet.add(id))
        configurationsSelected.value = Array.from(configurationsSelectedSet)
      }

      const handleDeselectAll = () => {
        configurationsSelected.value = []
      }

      const allCurrentPageSelected = computed(
        () => currentPageIds.value.length > 0
          && currentPageIds.value.every(id => selectedSet.value.has(id))
      )

      const masterCheckboxState = computed(() => {
        if (!hasAnySelected.value) return false
        if (allCurrentPageSelected.value) return true
        return 'indeterminate'
      })

      const handleMasterCheckboxClick = () => {
        if (allCurrentPageSelected.value) handleDeselectAll()
        else handleSelectAllCurrentPage()
      }

      const buildUrlSearchParams = () => {
        const params = new URLSearchParams()
        params.append('page', pagination.currentPage - 1)
        params.append('size', pagination.pageSize)
        params.append('sortBy', table.sort?.sortBy || 'modifiedDate')
        params.append('sortDirection', table.sort?.sortDirection || 'DESC')
        params.append('excludeFields', 'windChillData')
        params.append('excludeFields', 'ignoreList')
        params.append('excludeFields', 'restorePoints')
        params.append('excludeFields', 'selectedChoices')

        configurationsSearchString.value && params.append('search', configurationsSearchString.value)

        return params.toString()
      }

      const fetchConfigurationResults = async () => {
        areConfigurationsLoading.value = true
        const params = buildUrlSearchParams()

        const fetchConfigurationResultsPromise = () => getConfigurations(params)
        const data = await tableCallbacks.fetchTableData(fetchConfigurationResultsPromise)

        pagination.totalPages = data.totalPages || 1
        totalConfigurations.value = data.totalElements
        areConfigurationsLoading.value = false
      }

      const handleConfigurationDeletedEvent = async () => {
        await fetchConfigurationResults()
        recordAnalytics(PAC_DELETED_CONFIG)
      }

      const openDeleteConfigurationDialog = configuration => {
        configurationToDelete.value = { id: configuration.id, name: configuration.name }
        isDeleteConfigurationDialogShown.value = true
      }

      const openConfiguration = (id = '') => {
        router.push({ name: PAC_CONFIGURATION_ROUTE.name, params: { id } })
      }

      watch(() => pagination.currentPage, () => {
        fetchConfigurationResults()
      })

      watch(() => [configurationsSearchString.value, table.sort], () => {
        pagination.currentPage === 1 ? fetchConfigurationResults() : handlePaginationChange(1)
      })

      onMounted(async () => {
        await fetchConfigurationResults()
      })

      onUnmounted(() => {
        commit(usePacMutation(SET_CONFIGURATIONS_SEARCH_STRING), '')
      })

      return {
        table,
        pagination,
        handlePaginationChange,
        areConfigurationsLoading,
        handleConfigurationDeletedEvent,
        configurationToDelete,
        openDeleteConfigurationDialog,
        isDeleteConfigurationDialogShown,
        openConfiguration,
        configurationsSearchString,
        tableCallbacks,
        hasPermissionToDelete,
        showLearnMoreModal,
        showAddConfigurationsToListModal,
        configurationsSelected,
        isSelected,
        toggleSelection,
        handleSelectAllCurrentPage,
        handleSelectMineOnly,
        handleSelectFromToday,
        handleDeselectAll,
        currentPageCount,
        selectedOnCurrentPageCount,
        hasAnySelected,
        masterCheckboxState,
        handleMasterCheckboxClick,
        totalConfigurations,
        showSaveNewListModal,
        buildUrlSearchParams,
        savedListsModal
      }
    }
  }
  </script>

<style lang="scss" scoped>
  .configurations-table {
    min-height: 50vh;
    max-height: 75vh;

    &__pagination {
      display: flex;
      justify-content: center;
      margin-bottom: 20px;
    }

    &__description {
      white-space: normal;
    }

    // stylelint-disable declaration-no-important
    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline).disabled,
    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline)[disabled] {
      opacity: 0 !important;
    }

    tr:hover .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline).disabled,
    tr:hover .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline)[disabled] {
      opacity: 1 !important;
    }

    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline).disabled:hover,
    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline)[disabled]:hover {
      background-color: $mi-silver-200 !important;
    }
  }

  .configurations-table-header {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    &__top-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    &__master-checkbox {
      display: flex;
      align-items: center;
      margin-top: 8px;
    }

    &__master-control {
      display: flex;
      align-items: center;
      padding: 12px 15px;
      border-radius: 10% !important;

      .mi-checkbox {
        ::v-deep(.q-checkbox__label),
        ::v-deep(.q-anchor--skip) {
          display: none;
        }

        ::v-deep(.q-checkbox__inner--indeterminate .q-checkbox__bg) {
          background-color: $mi-white !important;
          border-color: $mi-anthracite-800 !important;
          color: $mi-anthracite-800 !important;
        }

        &:hover {
          background-color: $mi-silver-100;
          border-radius: 4px;
        }
      }
    }

    &__master-dropdown {
      min-height: unset !important;
      padding: 0 !important;
      border-radius: 25% !important;
      width: 22px !important;
      height: 22px !important;
      margin-left: 4px;
      display: flex;
      align-items: center;

      ::v-deep(.q-focus-helper) {
        display: none !important;
      }

      &:hover,
      &--active {
        background-color: $mi-silver-100 !important;
        color: $mi-anthracite-800 !important;
      }
    }

    &__action-buttons {
      display: flex;
      gap: 8px;
    }

    &__select-dropdown {
      display: flex;
      flex-direction: column;

      .mi-btn {
        font-family: "Man Europe", sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 150%;
        align-items: start;
        width: 100%;
      }
    }

    &__title {
      font-family: "Man Europe", sans-serif;
      font-weight: 700;
      font-size: 20px;
      line-height: 30px;
      letter-spacing: 0;
      text-transform: capitalize;
    }
  }
</style>
